import type { ColumnConfig } from '@/entities/data-export'
import type { ExportFieldsFormData } from './ExportFieldsModal'
import type { ExportParamsFormData } from './ExportParamsModal'

import React, { useMemo } from 'react'
import { kebabCase, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useLocalStorage } from 'usehooks-ts'

import { ampli } from '@/shared/lib/ampli'
import { download } from '@/shared/tools/report'

import { createExcelFile, mergeConfigs } from '../lib/helpers'
import ExportConfigurationProvider from './ExportConfigurationProvider'
import ExportModal from './ExportModal'

type ExportAdditionalDataModalProps = {
	kind: string
	fields: ColumnConfig[]
	rows: Record<string, unknown>[]
	defaultName?: string
	isOpen: boolean
	close(): void
}
export default function ExportAdditionalDataModal(
	props: ExportAdditionalDataModalProps
) {
	const { t } = useTranslation()

	const [customExportColumns, setCustomExportColumns] = useLocalStorage<
		ColumnConfig[]
	>(`export_fields_${props.kind}`, [])
	const fieldsPresetOptions = useMemo(
		() => [
			{
				value: 'all',
				label: t('app.all_columns', { defaultValue: 'All columns' }),
				preset: props.fields,
			},
			{
				value: 'custom',
				label: t('app.custom_columns', { defaultValue: 'Custom columns' }),
				preset: mergeConfigs(props.fields || [], customExportColumns || []),
			},
		],
		[props.fields, customExportColumns]
	)
	const handleSubmit = async (
		formData: ExportParamsFormData & ExportFieldsFormData
	) => {
		setCustomExportColumns(formData.fields as ColumnConfig[])
		ampli.exportModalCreateExport({
			export_kind: props.kind,
			name: formData.name,
			fields_preset: formData.fields_preset,
			format: formData.format,
			aggregation: formData.aggregation,
			fields: formData.fields.map((f) => f.key),
		})
		const filename = `${kebabCase(formData.name)}.${formData.format}`

		const blob = await createExcelFile({
			name: formData.name,
			fields: formData.fields.filter(prop('enabled')) as ColumnConfig[],
			rows: props.rows,
			format: formData.format,
		})
		download(blob, filename)
		toast.success(
			t('file.downloading.started', {
				filename,
				defaultValue: `File "${filename}" downloading started`,
			}),
			{ id: 'export' }
		)
		props.close()
	}

	return (
		<ExportConfigurationProvider
			kind={props.kind}
			isAdditional
			schema={[]}
			defaultValues={{
				name: props.defaultName || 'Report',
			}}
			fieldsPresetOptions={fieldsPresetOptions}
			onSubmit={handleSubmit}
		>
			<ExportModal isOpen={props.isOpen} close={props.close} />
		</ExportConfigurationProvider>
	)
}
